'use client';

import { useTenantOptional } from '@/contexts/TenantContext';
import { useEffect, useState, type ReactElement } from 'react';

/**
 * Custom Scripts Component
 *
 * Renders tenant-specific custom scripts from the analytics configuration.
 * These scripts are configured in Route Royal and can include:
 * - Custom tracking code
 * - Third-party integrations not covered by built-in providers
 * - Custom analytics implementations
 *
 * Security considerations:
 * - Scripts are validated and sanitised in Route Royal before storage
 * - enableCustomScripts must be true for scripts to render
 * - Scripts are only loaded after consent if required
 */

interface CustomScriptsProps {
  /** Position in the document */
  position: 'head' | 'body-start' | 'body-end';
  /** Whether cookie consent has been given. When omitted, reads from localStorage. */
  consentGiven?: boolean;
}

function readConsentFromStorage(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const consent = localStorage.getItem('cookie-consent');
    if (consent === 'accepted') return true;
    if (consent === 'rejected') return false;
    const prefs = localStorage.getItem('cookie-preferences');
    if (prefs) {
      const parsed = JSON.parse(prefs);
      return parsed.marketing === true || parsed.analytics === true;
    }
  } catch {
    // Storage access failed
  }
  return false;
}

/**
 * Server-side component for head scripts (rendered via dangerouslySetInnerHTML)
 * This is used in the layout for head scripts that need to be SSR'd
 */
export function CustomHeadScripts(): ReactElement | null {
  const tenant = useTenantOptional();

  // Get custom scripts from site config
  const siteConfig = tenant?.siteConfig;
  const analytics = siteConfig?.analytics as {
    customScripts?: {
      enabled?: boolean;
      headScripts?: string | null;
    };
  } | undefined;

  // Don't render if custom scripts are not enabled
  if (!analytics?.customScripts?.enabled) {
    return null;
  }

  const headScripts = analytics.customScripts.headScripts;
  if (!headScripts) {
    return null;
  }

  return (
    <script
      id="custom-head-scripts"
      dangerouslySetInnerHTML={{ __html: headScripts }}
    />
  );
}

/**
 * Client-side component for body scripts
 * Handles consent checking and dynamic script injection
 */
export function CustomScripts({ position, consentGiven }: CustomScriptsProps) {
  const tenant = useTenantOptional();
  const [shouldRender, setShouldRender] = useState(false);

  // Get custom scripts configuration
  const siteConfig = tenant?.siteConfig;
  const analytics = tenant?.analytics;
  const customScripts = (siteConfig?.analytics as {
    customScripts?: {
      enabled?: boolean;
      headScripts?: string | null;
      bodyStartScripts?: string | null;
      bodyEndScripts?: string | null;
    };
  })?.customScripts;

  useEffect(() => {
    if (!customScripts?.enabled) {
      return;
    }

    const requiresConsent = analytics?.consent?.required ?? false;
    if (requiresConsent) {
      // If consentGiven was explicitly passed, use it; otherwise read from localStorage
      const hasConsent = consentGiven ?? readConsentFromStorage();
      if (!hasConsent) {
        setShouldRender(false);
        return;
      }
    }

    setShouldRender(true);
  }, [customScripts?.enabled, analytics?.consent?.required, consentGiven]);

  // Re-evaluate when consent changes
  useEffect(() => {
    const handleConsentChange = () => {
      if (!customScripts?.enabled) return;
      const requiresConsent = analytics?.consent?.required ?? false;
      if (requiresConsent) {
        setShouldRender(readConsentFromStorage());
      }
    };

    window.addEventListener('cookie-consent-change', handleConsentChange);
    return () => window.removeEventListener('cookie-consent-change', handleConsentChange);
  }, [customScripts?.enabled, analytics?.consent?.required]);

  if (!shouldRender || !customScripts?.enabled) {
    return null;
  }

  // Get the appropriate script content based on position
  let scriptContent: string | null | undefined;
  switch (position) {
    case 'head':
      scriptContent = customScripts.headScripts;
      break;
    case 'body-start':
      scriptContent = customScripts.bodyStartScripts;
      break;
    case 'body-end':
      scriptContent = customScripts.bodyEndScripts;
      break;
  }

  if (!scriptContent) {
    return null;
  }

  return (
    <div
      id={`custom-scripts-${position}`}
      dangerouslySetInnerHTML={{ __html: scriptContent }}
    />
  );
}

/**
 * Hook to get custom scripts configuration
 */
export function useCustomScripts() {
  const tenant = useTenantOptional();
  const siteConfig = tenant?.siteConfig;

  const customScripts = (siteConfig?.analytics as {
    customScripts?: {
      enabled?: boolean;
      headScripts?: string | null;
      bodyStartScripts?: string | null;
      bodyEndScripts?: string | null;
    };
  })?.customScripts;

  return {
    enabled: customScripts?.enabled ?? false,
    headScripts: customScripts?.headScripts ?? null,
    bodyStartScripts: customScripts?.bodyStartScripts ?? null,
    bodyEndScripts: customScripts?.bodyEndScripts ?? null,
  };
}
